import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { CATEGORIES, ZONES, POPULAR_MEDICINES } from "@/lib/constants";
import { URGENCY_OPTIONS } from "@/lib/serviceConstants";
import { Send, Target, Sparkles, Info, Check, Pill } from "lucide-react";
import { toast } from "sonner";
import MedicineSelector from "@/components/MedicineSelector";

export default function MedicineRequestPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    medicine_name: "",
    category: "",
    zone: "",
    whatsapp: "",
    max_price: "",
    urgency: "normal",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Prefill from query params (e.g., /buscar-medicamento?prefill=Paracetamol&category=Analg%C3%A9sicos)
  useEffect(() => {
    const pre = searchParams.get("prefill");
    const cat = searchParams.get("category");
    if (pre || cat) {
      setForm((f) => ({
        ...f,
        medicine_name: pre || f.medicine_name,
        category: cat || f.category,
      }));
    }
  }, [searchParams]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.medicine_name) return toast.error("Selecciona el medicamento que buscas");
    if (!form.category) return toast.error("Selecciona la categoría");
    if (!form.zone) return toast.error("Selecciona tu zona");
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        max_price: form.max_price ? parseFloat(form.max_price) : null,
      };
      await api.post("/medicine-requests", payload);
      toast.success("Búsqueda publicada. Te avisaremos si alguien lo ofrece.");
      navigate("/busquedas");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo publicar la búsqueda");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-slate-50 min-h-[80vh]">
      <section className="bg-gradient-to-b from-amber-50 to-slate-50 border-b border-amber-100">
        <div className="max-w-3xl mx-auto px-4 py-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-amber-200 text-amber-800 text-xs font-medium">
            <Target className="w-3.5 h-3.5" /> Tablero de búsquedas
          </div>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-blue-950 tracking-tight">
            ¿No lo encontraste? <span className="text-blue-600">Publica tu búsqueda</span>.
          </h1>
          <p className="mt-3 text-slate-700 text-base sm:text-lg max-w-2xl">
            Avisa a la comunidad qué medicamento necesitas. Si alguien lo publica,
            te llega una notificación al instante y puedes contactarlo por WhatsApp.
          </p>
          <div className="mt-4 inline-flex items-center gap-2 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <Sparkles className="w-4 h-4" />
            Las búsquedas son públicas — cualquier vendedor puede verlas y ofrecerte el medicamento.
          </div>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <form onSubmit={onSubmit} className="space-y-5">
          <Card className="p-5 sm:p-6 rounded-2xl border border-slate-200 space-y-4">
            <h2 className="font-display text-lg font-semibold text-slate-900">¿Qué medicamento buscas?</h2>

            <div>
              <Label className="text-sm font-medium text-slate-800">¿Qué medicamento necesitas?</Label>
              <p className="text-xs text-slate-500 mt-0.5">Toca uno de los más comunes para evitar escribir.</p>

              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="med-req-popular-grid">
                {POPULAR_MEDICINES.map((m) => {
                  const active = form.medicine_name === m.name;
                  return (
                    <button
                      key={m.name}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, medicine_name: m.name, category: m.category }))}
                      className={`text-left p-3 rounded-xl border-2 transition-colors btn-press ${
                        active
                          ? "border-blue-600 bg-blue-50"
                          : "border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50/50"
                      }`}
                      data-testid={`med-req-popular-chip-${m.name}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className={`text-sm leading-tight ${active ? "font-semibold text-blue-900" : "font-medium text-slate-900"}`}>{m.name}</div>
                          <div className="text-[11px] text-slate-500 mt-0.5">{m.category}</div>
                        </div>
                        {active && <Check className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-xs text-slate-500 mb-2">¿No está en la lista? Elige del catálogo completo:</p>
                <MedicineSelector
                  value={POPULAR_MEDICINES.some((m) => m.name === form.medicine_name) ? "" : form.medicine_name}
                  category={form.category}
                  placeholder="Ver catálogo completo y elegir…"
                  onSelect={({ medicine_name, category }) => {
                    setForm((f) => ({
                      ...f,
                      medicine_name,
                      category: category || f.category,
                    }));
                  }}
                />
              </div>

              {form.medicine_name && (
                <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 flex items-center gap-2" data-testid="med-req-selected-pill">
                  <Pill className="w-4 h-4 text-blue-700 shrink-0" />
                  <div className="flex-1 min-w-0 text-sm">
                    <span className="text-slate-600">Buscas:</span> <strong className="text-blue-900">{form.medicine_name}</strong>
                    {form.category && <span className="text-slate-500"> · {form.category}</span>}
                  </div>
                </div>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-slate-800">Categoría</Label>
                <Select value={form.category} onValueChange={(v) => setField("category", v)}>
                  <SelectTrigger className="h-12 mt-1.5 text-base bg-slate-50" data-testid="med-req-category-select">
                    <SelectValue placeholder="Selecciona…" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-800">Urgencia</Label>
                <Select value={form.urgency} onValueChange={(v) => setField("urgency", v)}>
                  <SelectTrigger className="h-12 mt-1.5 text-base bg-slate-50" data-testid="med-req-urgency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {URGENCY_OPTIONS.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-slate-800">Zona donde lo recibirías</Label>
                <Select value={form.zone} onValueChange={(v) => setField("zone", v)}>
                  <SelectTrigger className="h-12 mt-1.5 text-base bg-slate-50" data-testid="med-req-zone-select">
                    <SelectValue placeholder="Selecciona…" />
                  </SelectTrigger>
                  <SelectContent>
                    {ZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-800">Precio máximo dispuesto a pagar (opcional)</Label>
                <Input type="number" min={0} step="0.01" value={form.max_price} onChange={(e) => setField("max_price", e.target.value)} className="h-12 mt-1.5 text-base bg-slate-50" placeholder="$0 = sin límite" data-testid="med-req-max-price-input" />
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium text-slate-800">Tu WhatsApp (con código de país)</Label>
              <Input required value={form.whatsapp} onChange={(e) => setField("whatsapp", e.target.value.replace(/[^0-9+ ]/g, ""))} className="h-12 mt-1.5 text-base bg-slate-50" placeholder="+52 55 1234 5678" data-testid="med-req-whatsapp-input" />
              <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1"><Info className="w-3 h-3"/>Los vendedores podrán contactarte directamente.</p>
            </div>

            <div>
              <Label className="text-sm font-medium text-slate-800">Detalles adicionales (opcional)</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => setField("notes", e.target.value)} className="mt-1.5 text-base bg-slate-50" placeholder="Cuántas piezas necesitas, dosis específica, etc." data-testid="med-req-notes-textarea" />
            </div>
          </Card>

          <Button type="submit" disabled={submitting} className="w-full h-14 text-base bg-blue-600 hover:bg-blue-700 rounded-xl btn-press shadow-md" data-testid="med-req-submit-btn">
            <Send className="w-5 h-5 mr-2" />
            {submitting ? "Publicando…" : "Publicar mi búsqueda"}
          </Button>
        </form>
      </div>
    </div>
  );
}
